import * as Sentry from "@sentry/react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";

import { PostHogTestButton } from "./components/debug/PostHogTestButton";
import { SentryTestButton } from "./components/debug/SentryTestButton";
import { TailwindIndicator } from "./components/debug/TailwindIndicator";
import ErrorBoundary from "./components/ErrorBoundary";
import Navigation from "./components/Navigation";
import { AuthProvider } from "./components/providers/AuthProvider";
import { PostHogUserProvider } from "./components/providers/PostHogUserProvider";
import Auth from "./pages/Auth";
import AuthError from "./pages/AuthError";
import CheckoutProduct from "./pages/CheckoutProduct";
import CheckoutSubscription from "./pages/CheckoutSubscription";
import EmailVerified from "./pages/EmailVerified";
import ForgotPassword from "./pages/ForgotPassword";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";
import Purchase from "./pages/Purchase";
import Stripe from "./pages/Stripe";
import StripeCheckout from "./pages/StripeCheckout";
import StripeReturn from "./pages/StripeReturn";
import VerifyEmail from "./pages/VerifyEmail";
import { TRPCProvider } from "./utils/api";

function AppContent() {
  const location = useLocation();

  // Show navigation on home and auth pages
  // In development mode, also show navigation on purchase page
  const showNavigation =
    location.pathname === "/" ||
    location.pathname === "/auth" ||
    (import.meta.env.DEV && location.pathname === "/purchase");

  return (
    <div className="min-w-screen min-h-screen flex flex-col relative">
      {showNavigation && <Navigation />}
      <TailwindIndicator />
      <main>
        <SentryRoutes>
          {/* PROFILE PAGE, OR REDIRECT TO /AUTH */}
          <Route path="/" element={<Home />} />
          {/* PURCHASE PAGE FROM MARKETING WEBSITE */}
          <Route path="/purchase" element={<Purchase />} />
          {/* STRIPE CHECKOUT PAGES */}
          <Route path="/stripe" element={<Stripe />} />
          <Route path="/stripe-checkout" element={<StripeCheckout />} />
          <Route path="/stripe-return" element={<StripeReturn />} />
          {/* AUTH PAGES */}
          <Route path="/auth" element={<Auth />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/onboarding/verify-email" element={<VerifyEmail />} />
          <Route path="/v2/static/email-verified" element={<EmailVerified />} />
          <Route path="/auth/error" element={<AuthError />} />
          {/* SHOPIFY / REVENUECAT REDIRECT */}
          <Route
            path="/checkout/subscription/:packageId"
            element={<CheckoutSubscription />}
          />
          <Route
            path="/checkout/product/:handle"
            element={<CheckoutProduct />}
          />
          {/* DEBUG PAGES */}
          {/* <Route path="/profile" element={<Profile />} /> */}
          {/* <Route path="/products" element={<Products />} /> */}
          {/* <Route path="/products/:handle" element={<ProductDetail />} /> */}
          {/* Fallback route for general subscription checkout */}
          {/* <Route path="/checkout/subscription" element={<Profile />} /> */}

          {/* Redirect legacy/unused login route to home */}
          <Route path="/login" element={<Navigate to="/" replace />} />

          {/* Catch-all route for 404 errors - must be last */}
          <Route path="*" element={<NotFound />} />
        </SentryRoutes>
      </main>
    </div>
  );
}

// Wrap components with Sentry for better error tracking
const SentryRoutes = Sentry.withSentryRouting(Routes);

function App() {
  return (
    <ErrorBoundary>
      <TRPCProvider>
        <TailwindIndicator />
        <Router>
          <AuthProvider>
            <PostHogUserProvider>
              <AppContent />
              <SentryTestButton />
              <PostHogTestButton />
            </PostHogUserProvider>
          </AuthProvider>
        </Router>
      </TRPCProvider>
    </ErrorBoundary>
  );
}

export default App;
